import React from 'react';
import { Theme, useTheme, styled } from '@mui/material/styles';
import { LocalAudioTrack, LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';
import { keyframes } from '@emotion/react';

import AudioLevelIndicator from '../AudioLevelIndicator/AudioLevelIndicator';
// import AvatarIcon from '../../icons/AvatarIcon';
import NetworkQualityLevel from '../NetworkQualityLevel/NetworkQualityLevel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import useIsRecording from '../../hooks/useIsRecording/useIsRecording';
import useIsTrackSwitchedOff from '../../hooks/useIsTrackSwitchedOff/useIsTrackSwitchedOff';
import useParticipantIsReconnecting from '../../hooks/useParticipantIsReconnecting/useParticipantIsReconnecting';
import usePublications from '../../hooks/usePublications/usePublications';
import useScreenShareParticipant from '../../hooks/useScreenShareParticipant/useScreenShareParticipant';
import useTrack from '../../hooks/useTrack/useTrack';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useParticipants from '../../hooks/useParticipants/useParticipants';

import { getIdentityInitials, removeIdentityMasking } from '../../utils';

const pulsate = keyframes`
  0% {
    background: #A90000;
  }
  50% {
    background: #f00;
  }
  100% {
    background: #A90000;
  }
`;

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isFullWidth' && prop !== 'hasParticipants',
})<{ isFullWidth: boolean; hasParticipants: boolean }>(({ theme, isFullWidth, hasParticipants }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gridArea: '1 / 1 / 2 / 3',
  top: '0px',
  height: '87%',
  ...(hasParticipants && {
    top: '22%',
    height: '64%',
  }),
  ...(isFullWidth && {
    gridArea: '1 / 1 / 2 / 3',
    [theme.breakpoints.down('sm')]: {
      gridArea: '1 / 1 / 3 / 3',
    },
  }),
}));

const Identity = styled('div')({
  background: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  padding: '0.1em 0.3em 0.1em 0',
  display: 'inline-flex',
  '& svg': {
    marginLeft: '0.3em',
  },
  marginRight: '0.4em',
  alignItems: 'center',
  borderRadius: '10px',
});

const InfoContainer = styled('div')({
  position: 'absolute',
  zIndex: 2,
  height: '100%',
  width: '100%',
});

const IdentityInfo = styled('div')({
  display: 'flex',
  position: 'absolute',
  bottom: '0px',
});

const ReconnectingContainer = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(40, 42, 43, 0.75)',
  zIndex: 1,
});

const AvatarContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#191b1d',
  position: 'absolute',
  color: 'white',
  fontSize: 'xxx-large',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1,
  '& svg': {
    transform: 'scale(2)',
  },
});

const RecordingIndicator = styled('div')(({ theme }) => ({
  right: 0,
  position: 'absolute',
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  padding: '0.1em 0.3em 0.1em 0',
  fontSize: '1.2rem',
  height: '28px',
  [theme.breakpoints.down('sm')]: {
    bottom: 'auto',
    right: 0,
    top: 0,
  },
}));

const Circle = styled('div')({
  height: '12px',
  width: '12px',
  background: 'red',
  borderRadius: '100%',
  margin: '0 0.6em',
  animation: `${pulsate} 1.25s ease-out infinite`,
});

interface MainParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
}

export default function MainParticipantInfo({ participant, children }: MainParticipantInfoProps) {
  const { room } = useVideoContext();
  const localParticipant = room!.localParticipant;
  const isLocal = localParticipant === participant;

  const participants = useParticipants();

  const screenShareParticipant = useScreenShareParticipant();
  // const isRemoteParticipantScreenSharing = screenShareParticipant && screenShareParticipant !== localParticipant;

  const publications = usePublications(participant);
  const videoPublication = publications.find(p => !p.trackName.includes('screen') && p.kind === 'video');
  const screenSharePublication = publications.find(p => p.trackName.includes('screen'));

  const videoTrack = useTrack(screenSharePublication || videoPublication);
  const isVideoEnabled = Boolean(videoTrack);

  const audioPublication = publications.find(p => p.kind === 'audio');
  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;

  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  const isRecording = useIsRecording();

  return (
    <Container
      data-cy-main-participant
      data-cy-participant={participant.identity}
      isFullWidth={!screenShareParticipant}
      hasParticipants={participants.length > 0}
    >
      <InfoContainer>
        <IdentityInfo>
          <Identity>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography variant="body1" color="inherit">
              {/* {participant.identity} */}
              {removeIdentityMasking(participant.identity)}
              {isLocal && ' (You)'}
              {screenSharePublication && ' - Screen'}
            </Typography>
          </Identity>
          <NetworkQualityLevel participant={participant} />
        </IdentityInfo>
        {isRecording && (
          <Tooltip
            title="All participants' audio and video is currently being recorded. Visit the app settings to stop recording."
            placement="top"
          >
            <RecordingIndicator>
              <Circle />
              <Typography variant="body1" color="inherit" data-cy-recording-indicator>
                Recording
              </Typography>
            </RecordingIndicator>
          </Tooltip>
        )}
      </InfoContainer>
      {(!isVideoEnabled || isVideoSwitchedOff) && (
        <AvatarContainer>
          {/* <AvatarIcon /> */}
          {getIdentityInitials(participant.identity)}
        </AvatarContainer>
      )}
      {isParticipantReconnecting && (
        <ReconnectingContainer>
          <Typography variant="body1" style={{ color: 'white' }}>
            Reconnecting...
          </Typography>
        </ReconnectingContainer>
      )}
      {children}
    </Container>
  );
}
